import { useCallback } from "react";
import type { AppDispatch } from "../../store";
import type { ShipmentUI } from "../../store/shipmentSlice";
import type { ReferenceNames } from "@services/shipments/types";
import {
  updateTemporaryShipmentEdit,
  setActiveEdit,
  updateShipmentProperty,
  updateTemporaryShipmentProperty,
} from "../../store/shipmentSlice";

/**
 * Custom hook that encapsulates table cell editing logic
 *
 * Provides handlers for:
 * - Cell value changes (keystroke updates)
 * - Edit start/end lifecycle
 * - Saving committed edits
 *
 * Handles both temporary (unsaved) and regular shipments differently
 */
export function useTableCellEditing(dispatch: AppDispatch) {
  // Handle cell change (keystroke updates for temporary rows only)
  const handleCellChange = useCallback(
    (
      row: ShipmentUI,
      columnName: string,
      oldValue: any,
      newValue: any,
      cursorPosition: number
    ) => {
      dispatch(
        updateTemporaryShipmentEdit({
          tempId: row.id,
          attribute: columnName as ReferenceNames,
          value: String(newValue),
          cursorPosition,
        })
      );
    },
    [dispatch]
  );

  // Handle cell edit start (set active edit state)
  const handleCellEditStart = useCallback(
    (row: ShipmentUI, columnName: string) => {
      dispatch(
        setActiveEdit({
          tempId: row.id,
          columnField: columnName,
          cursorPosition: 0,
          selectAll: true, // Select all on initial click
        })
      );
    },
    [dispatch]
  );

  // Handle cell edit end (clear active edit state only if this cell is active)
  const handleCellEditEnd = useCallback(
    (row: ShipmentUI, columnName: string) => {
      // Read the LATEST activeEdit from Redux to avoid stale closure
      const currentActiveEdit = (window as any).__REDUX_STORE__?.getState?.()
        ?.shipment?.shipments?.temporaryShipmentEdits?.activeEdit;

      // Only clear if this cell is currently the active one
      if (
        currentActiveEdit?.tempId === row.id &&
        currentActiveEdit?.columnField === columnName
      ) {
        dispatch(setActiveEdit(null));
      }
    },
    [dispatch]
  );

  // Handle cell save (committed edits for non-temporary rows only)
  const handleCellSave = useCallback(
    (row: ShipmentUI, columnName: string, oldValue: any, newValue: any) => {
      if (!row.isTemporary) {
        // Dispatch the update action with optimistic updates for regular shipments
        dispatch(
          updateShipmentProperty({
            shipmentId: row.id,
            attribute: columnName as ReferenceNames,
            newValue: String(newValue),
          })
        );
      } else {
        dispatch(
          updateTemporaryShipmentProperty({
            tempId: row.id,
            attribute: columnName as ReferenceNames,
            value: String(newValue),
          })
        );
      }
    },
    [dispatch]
  );

  return {
    handleCellChange,
    handleCellEditStart,
    handleCellEditEnd,
    handleCellSave,
  };
}
